import React from "react";
import { Check, ShieldCheck } from "lucide-react";
import {
  OFF_MARKET_SENTENCE_PLAIN,
  OFF_MARKET_SENTENCE_SMS,
  PREFERENCES_SENTENCE_PLAIN,
  PREFERENCES_SENTENCE_SMS,
  previewText,
} from "../utils/messageBlocks";

/**
 * The two lines that can ride along with a marketing message, plus the standing
 * promise about the unsubscribe link.
 *
 * Christian asked for exactly this card on every composer (CG, 2026-08-28: "I
 * really like these buttons though so please add it to both email pages" and,
 * of the Find Buyers text modal, "we like the Invite to the off market portal &
 * let them update their preferences buttons that we want to add to both pages
 * also"). It is one component so Find Buyers email, Find Buyers text, Campaign
 * email and Campaign text cannot drift apart again.
 *
 * The card has TWO modes, and which one it is in is not the agent's decision
 * (CG, Monday 13056948901, 2026-09-16: "Sorry dont make it mandatory by default
 * for emails & texts. Only make it mandatory if an agent populates from a
 * property"):
 *
 *   locked=false  tick boxes, the agent's call. This is the default, and it is
 *                 what a plain campaign to a list gets.
 *   locked=true   green ticks and a statement, no way to switch either off.
 *                 Used when the message was built FROM a property - Find Buyers
 *                 for a listing, a campaign with a property picked, the
 *                 Enquiries/Buyers composers on a property page. That is a
 *                 message about a specific listing going to people who should
 *                 be offered the portal and their own preferences link.
 *
 * Briefly (16 Sep 2026, morning) it was locked everywhere. Christian reversed
 * that the same day, hence the two modes rather than a straight revert.
 */
export default function MessageExtras({
  channel = "email",
  offMarket,
  onOffMarketChange,
  preferences,
  onPreferencesChange,
  offMarketInBody = false,
  preferencesInBody = false,
  // SMS only: what ticking the preferences box adds to the bill.
  preferencesExtraCredits = 0,
  recipientCount = 0,
  // Hidden where a per-recipient preferences link can't be built (e.g. a
  // property's enquirers, who have no saved-search record to point at). The
  // Off-Market invite still works for anyone, so it always shows.
  showPreferences = true,
  // Both blocks go on and neither can be switched off. See the note above.
  locked = false,
  className = "",
}) {
  const isEmail = channel !== "sms";

  // Who actually receives each line is decided per person at send time, not
  // here (communications.services.portal_membership - MP, Monday 13056948901,
  // 21 Sep 2026: "If they're already in the portal, don't send them the portal
  // invitation ... If they're not in the portal, don't send them the updated
  // preferences"). The agent is told, because otherwise the first person to
  // compare a sent email against the preview below reads it as a fault, which
  // is exactly how this line became a ticket the last two times.
  const ONLY_NON_MEMBERS = " Only goes to people who are not on the portal yet.";
  const ONLY_MEMBERS = " Only goes to people who are already on the portal.";

  const offMarketNote = offMarketInBody
    ? "You have already put {{OffMarketPortalLink}} in your message above, so it is not added a second time."
    : (isEmail
      ? "A line under your message, above Kind regards, with the link on \"Join the Collings platform\". It opens collings.com.au/portal, where they can sign up."
      : "Added to the end of the text, on its own line. The link opens collings.com.au/portal, where they can sign up.") + ONLY_NON_MEMBERS;

  const preferencesNote = preferencesInBody
    ? "You have already put {{PreferencesLink}} in your message above, so it is not added a second time."
    : (isEmail
      ? "The line under that one, with the link on \"Update your property preferences\". Each recipient gets their own, to change suburbs, price and beds. No login needed."
      : "Each recipient gets their own link to change suburbs, price and beds. No login needed.") + ONLY_MEMBERS;

  const offMarketQuote = isEmail
    ? `"${OFF_MARKET_SENTENCE_PLAIN}"`
    : `"${previewText(OFF_MARKET_SENTENCE_SMS)}"`;

  const preferencesQuote = isEmail
    ? `"${PREFERENCES_SENTENCE_PLAIN}"`
    : `"${PREFERENCES_SENTENCE_SMS.replace("{{PreferencesLink}}", "their own preferences link")}"`;

  // The SMS-only warning that the preferences link tips this message over a
  // segment. Shown in both modes: locked or not, the agent is about to pay for
  // it and should see that before they send to a few thousand people.
  const creditWarning = !isEmail && !preferencesInBody && preferencesExtraCredits > 0 ? (
    <span className="block text-[11px] text-amber-600 mt-0.5">
      {`Costs about ${preferencesExtraCredits} extra credit${preferencesExtraCredits !== 1 ? "s" : ""} per text`
        + `${recipientCount > 0 ? ` (about ${(preferencesExtraCredits * recipientCount).toLocaleString()} for this send)` : ""}`
        + ", because it pushes the message over a segment. It is free on email."}
    </span>
  ) : null;

  return (
    <div className={`rounded-lg border border-gray-200 p-3 space-y-2 ${className}`}>
      {locked && (
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
          Always added to this {isEmail ? "email" : "text"}, because it is about a property
        </p>
      )}

      {locked ? (
        <div className="flex items-start gap-2">
          <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
          <span className="text-xs text-gray-600">
            <span className="font-semibold text-gray-700">Invite them to the Off Market Portal</span>
            <span className="block text-[11px] text-gray-500 mt-0.5">{offMarketQuote}</span>
            <span className="block text-[11px] text-gray-400 mt-0.5">{offMarketNote}</span>
          </span>
        </div>
      ) : (
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!offMarket}
            onChange={(e) => onOffMarketChange(e.target.checked)}
            className="mt-0.5 rounded border-gray-300"
          />
          <span className="text-xs text-gray-600">
            <span className="font-semibold text-gray-700">Invite them to the Off Market Portal</span>
            <span className="block text-[11px] text-gray-500 mt-0.5">{offMarketQuote}</span>
            <span className="block text-[11px] text-gray-400 mt-0.5">{offMarketNote}</span>
          </span>
        </label>
      )}

      {/* Separate from the invitation above, because they do different jobs:
          one signs a new buyer up, this one lets a buyer we already have retune
          what they get sent (CG, 2026-08-28: "Please put the preferences link
          back, that was really good"). */}
      {showPreferences && (locked ? (
        <div className="flex items-start gap-2">
          <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
          <span className="text-xs text-gray-600">
            <span className="font-semibold text-gray-700">Let them update their preferences</span>
            <span className="block text-[11px] text-gray-500 mt-0.5">{preferencesQuote}</span>
            <span className="block text-[11px] text-gray-400 mt-0.5">{preferencesNote}</span>
            {creditWarning}
          </span>
        </div>
      ) : (
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!preferences}
            onChange={(e) => onPreferencesChange(e.target.checked)}
            className="mt-0.5 rounded border-gray-300"
          />
          <span className="text-xs text-gray-600">
            <span className="font-semibold text-gray-700">Let them update their preferences</span>
            <span className="block text-[11px] text-gray-500 mt-0.5">{preferencesQuote}</span>
            <span className="block text-[11px] text-gray-400 mt-0.5">{preferencesNote}</span>
            {creditWarning}
          </span>
        </label>
      ))}

      <div className="flex items-start gap-2 text-xs text-gray-500">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
        <span>
          {isEmail
            ? "An Unsubscribe button is added to the bottom of every email automatically."
            : "A tap to stop unsubscribe link is added to every text automatically."}
        </span>
      </div>
      {!isEmail && (
        <div className="flex items-start gap-2 text-xs text-gray-500">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
          <span>
            Long links are shortened automatically (collings.com.au/l/...), so these lines cost
            a few characters instead of a few hundred.
          </span>
        </div>
      )}
    </div>
  );
}
